/**
 * Utility routine to scan localStorage for cached highlight data and video payloads
 * and clear entries older than 30 days (30 * 24 * 60 * 60 * 1000 ms) to optimize performance.
 */
export function cleanupExpiredHighlightCache(): { clearedCount: number; freedBytes: number } {
  const EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const now = Date.now();
  let clearedCount = 0;
  let freedBytes = 0;

  try {
    // 1. Check specific highlight cache keys in localStorage
    const highlightKeys = Object.keys(localStorage).filter(
      (key) => key.startsWith('playground_highlight_') || key.startsWith('cached_highlight_') || key.includes('_highlight_cache')
    );

    highlightKeys.forEach((key) => {
      try {
        const itemStr = localStorage.getItem(key);
        if (itemStr) {
          const parsed = JSON.parse(itemStr);
          const timestamp = parsed.timestamp || parsed.cachedAt || parsed.createdAtTimestamp;
          if (timestamp && (now - Number(timestamp) > EXPIRATION_MS)) {
            freedBytes += itemStr.length;
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      } catch (e) {
        // If JSON is invalid or old format without timestamp but marked expired
      }
    });

    // 2. Also inspect saved user highlights in playground_current_user if any clips are expired local blobs
    const savedUser = localStorage.getItem('playground_current_user');
    if (savedUser) {
      try {
        const userObj = JSON.parse(savedUser);
        if (userObj && Array.isArray(userObj.highlights)) {
          const initialLen = userObj.highlights.length;
          const validHighlights = userObj.highlights.filter((clip: any) => {
            if (!clip.timestamp) return true; // Keep if no timestamp attached
            return (now - Number(clip.timestamp)) <= EXPIRATION_MS;
          });
          if (validHighlights.length < initialLen) {
            userObj.highlights = validHighlights;
            localStorage.setItem('playground_current_user', JSON.stringify(userObj));
            clearedCount += (initialLen - validHighlights.length);
          }
        }
      } catch (e) {
        // Silent catch
      }
    }

    // Record last cleanup run timestamp
    localStorage.setItem('playground_last_highlight_cache_cleanup', now.toString());
  } catch (err) {
    console.error('Error executing expired highlight cache cleanup:', err);
  }

  return { clearedCount, freedBytes };
}
